import React, { useState, useEffect } from 'react';
import { Dropdown, Menu, Button, DatePicker } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { getSensorConnection } from '../../../../state/recordedData/api';

const { RangePicker } = DatePicker;

// Constants for duration keys
const DURATION_KEYS = {
  LATEST: 'latest',
  LAST_1H: 'last_1h',
  LAST_6H: 'last_6h',
  LAST_12H: 'last_12h',
  LAST_24H: 'last_24h',
  CUSTOM: 'custom_date',
};

// Default durations in milliseconds
const DEFAULT_DURATIONS: Record<string, number> = {
  [DURATION_KEYS.LATEST]: 0,
  [DURATION_KEYS.LAST_1H]: 1 * 60 * 60 * 1000,
  [DURATION_KEYS.LAST_6H]: 6 * 60 * 60 * 1000,
  [DURATION_KEYS.LAST_12H]: 12 * 60 * 60 * 1000,
  [DURATION_KEYS.LAST_24H]: 24 * 60 * 60 * 1000,
};

// Format duration key to a human-readable format
const formatDurationKey = (key: string) => key.replace('_', ' ').replace('last', 'Last');

interface DurationSelectorProps {
  patientKey: string;
  onChange: (duration: { key: string; startTime: number; endTime: number; isCustom: boolean }) => void;
  disabled?: boolean;
  dispatchMethod?: (params: { patientKey: string; startTime: number; endTime: number }) => any;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({ patientKey, onChange, disabled = false, dispatchMethod }) => {
  const dispatch = useDispatch();
  const [customDates, setCustomDates] = useState(null);
  const [customDateVisible, setCustomDateVisible] = useState(false);
  const [duration, setDuration] = useState({ key: DURATION_KEYS.LATEST, startTime: 0, endTime: 0, isCustom: false });

  const { data: sensorData } = useQuery(['sensorConnection', patientKey], () => getSensorConnection(patientKey), {
    enabled: duration.key === DURATION_KEYS.LATEST,
    staleTime: 100,
  });

  useEffect(() => {
    if (sensorData) {
      updateDuration(DURATION_KEYS.LATEST, sensorData.startTime, sensorData.endTime, false);
    }
  }, [sensorData]);

  const dispatchAction = (startTime: number, endTime: number) => {
    if (dispatchMethod) {
      dispatch(dispatchMethod({ patientKey, startTime, endTime }));
    }
  };

  const updateDuration = (key: string, startTime: number, endTime: number, isCustom: boolean) => {
    if (duration.startTime === startTime && duration.endTime === endTime) return;
    const newDuration = { key, startTime, endTime, isCustom };
    setDuration(newDuration);
    dispatchAction(startTime, endTime);
    onChange(newDuration);
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === DURATION_KEYS.CUSTOM) {
      setCustomDateVisible(true);
      return;
    }
    setCustomDateVisible(false);
    setCustomDates(null);
    
    const durationMs = DEFAULT_DURATIONS[key] || 0;
    const startTime = key === DURATION_KEYS.LATEST && sensorData ? sensorData.startTime : Date.now() - durationMs;
    const endTime = key === DURATION_KEYS.LATEST && sensorData ? sensorData.endTime : Date.now();
    
    updateDuration(key, startTime, endTime, false);
  };

  const handleDateChange = (dates: any) => {
    if (!dates) return;
    setCustomDates(dates);
    const startTime = dates[0].valueOf();
    const endTime = dates[1].valueOf();
    updateDuration(DURATION_KEYS.CUSTOM, startTime, endTime, true);
  };

  const renderMenu = (
    <Menu onClick={handleMenuClick} selectedKeys={[duration.key]}>
      {Object.keys(DEFAULT_DURATIONS).map((key) => (
        <Menu.Item key={key}>{formatDurationKey(key)}</Menu.Item>
      ))}
      <Menu.SubMenu key={DURATION_KEYS.CUSTOM} title="Custom Date">
        <div style={{ padding: '10px' }}>
          <RangePicker showTime format="DD-MMM-YY HH:mm:ss" value={customDates} onChange={handleDateChange} />
        </div>
      </Menu.SubMenu>
    </Menu>
  );

  return (
    <Dropdown overlay={renderMenu} trigger={['click']} disabled={disabled}>
      <Button disabled={disabled}>
        {formatDurationKey(duration.key)} <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export default DurationSelector;
